<template>
  <div>
    <section class="section is-main-section">
      <CardComponent>
        <div class="section">
          <h1 class="title">Activity Audit Log</h1>
          <p class="subtitle">
            View all user actions and changes within your organization
          </p>

          <b-field grouped group-multiline>
            <b-field label="Filter by User">
              <b-select v-model="filterUser" placeholder="All Users" @input="loadAuditLogs">
                <option :value="null">All Users</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.first_name }} {{ user.last_name }}
                </option>
              </b-select>
            </b-field>

            <b-field label="Filter by Action">
              <b-select v-model="filterAction" placeholder="All Actions" @input="loadAuditLogs">
                <option :value="null">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
              </b-select>
            </b-field>

            <b-field label="Filter by Entity">
              <b-select v-model="filterEntity" placeholder="All Entities" @input="loadAuditLogs">
                <option :value="null">All Entities</option>
                <option value="segments">Segments</option>
                <option value="episodes">Episodes</option>
                <option value="media">Media</option>
                <option value="schedules">Schedules</option>
                <option value="channels">Channels</option>
                <option value="users">Users</option>
              </b-select>
            </b-field>

            <b-field label=" ">
              <b-button type="is-primary" @click="loadAuditLogs"
                >Refresh</b-button
              >
            </b-field>
          </b-field>

          <b-field class="has-margin-top-15">
            <b-input
              v-model="search"
              placeholder="Search by user, action, or entity..."
              type="search"
              icon="magnify"
            >
            </b-input>
          </b-field>

          <b-table
            :data="filteredLogs"
            :bordered="false"
            :striped="true"
            :narrowed="false"
            :hoverable="true"
            :loading="loading"
            :mobile-cards="true"
            :paginated="true"
            :per-page="20"
            default-sort="created_at"
            default-sort-direction="desc"
          >
            <template slot-scope="props">
              <b-table-column
                field="created_at"
                label="Timestamp"
                sortable
                width="180"
              >
                <div style="font-size: 12px;">
                  {{ formatDate(props.row.created_at) }}<br />
                  <span style="color: #7a7a7a;">{{
                    formatTime(props.row.created_at)
                  }}</span>
                </div>
              </b-table-column>

              <b-table-column field="user" label="User" width="200">
                <div>
                  <strong>{{
                    props.row.user ? props.row.user.name : 'Unknown'
                  }}</strong
                  ><br />
                  <span style="font-size: 12px; color: #7a7a7a;">
                    {{ props.row.user ? props.row.user.email : '-' }}
                  </span>
                </div>
              </b-table-column>

              <b-table-column field="action" label="Action" width="100">
                <b-tag :type="getActionColor(props.row.action)">
                  {{ props.row.action }}
                </b-tag>
              </b-table-column>

              <b-table-column field="table_name" label="Entity" width="120">
                <div style="text-transform: capitalize;">
                  {{ props.row.table_name }}
                </div>
              </b-table-column>

              <b-table-column field="changes" label="Changes">
                <!-- Media Info for Schedules, Segments, Episodes, Media -->
                <div v-if="props.row.media_info" style="margin-bottom: 8px;">
                  <div style="font-weight: 600;">
                    {{ props.row.media_info.title }}
                  </div>
                  <div
                    v-if="
                      props.row.media_info.type === 'series' &&
                        props.row.media_info.series_no
                    "
                    style="font-size: 12px; color: #7a7a7a;"
                  >
                    Season {{ props.row.media_info.series_no }}, Episode
                    {{ props.row.media_info.episode_number }}
                    <span v-if="props.row.media_info.episode_title">
                      - {{ props.row.media_info.episode_title }}
                    </span>
                  </div>
                </div>

                <!-- Enriched Info for Channels, Users -->
                <div
                  v-if="props.row.enriched_info"
                  style="margin-bottom: 8px; font-weight: 600;"
                >
                  {{
                    props.row.enriched_info.channel_name ||
                      props.row.enriched_info.user_name
                  }}
                </div>

                <!-- Action Summary -->
                <div
                  v-if="props.row.action === 'update'"
                  style="font-size: 13px;"
                >
                  <template v-if="hasSegmentDurationChange(props.row)">
                    <b-tag type="is-warning" style="margin-bottom: 5px;">
                      Duration Changed
                    </b-tag>
                    <div
                      style="font-family: monospace; font-size: 11px; margin-top: 5px;"
                    >
                      {{
                        formatDuration(props.row.old_values.duration_seconds)
                      }}
                      →
                      {{
                        formatDuration(props.row.new_values.duration_seconds)
                      }}
                    </div>
                  </template>
                  <div
                    v-if="props.row.new_values && props.row.new_values.notes"
                    style="margin-top: 5px; font-style: italic; color: #666;"
                  >
                    {{ truncate(props.row.new_values.notes, 100) }}
                  </div>
                  <template v-if="!hasSegmentDurationChange(props.row)">
                    <span style="color: #7a7a7a;">
                      {{ countChanges(props.row) }} field(s) updated
                    </span>
                  </template>
                </div>
                <div
                  v-else-if="props.row.action === 'create'"
                  style="color: #48c774;"
                >
                  Created new {{ props.row.table_name }}
                </div>
                <div
                  v-else-if="props.row.action === 'delete'"
                  style="color: #f14668;"
                >
                  Deleted {{ props.row.table_name }}
                </div>
              </b-table-column>

              <b-table-column label="Details" width="100">
                <b-button
                  size="is-small"
                  type="is-info"
                  outlined
                  @click="showDetails(props.row)"
                >
                  View Details
                </b-button>
              </b-table-column>
            </template>

            <template slot="empty">
              <div class="has-text-centered">
                No audit logs found.
              </div>
            </template>
          </b-table>
        </div>
      </CardComponent>
    </section>

    <!-- Details Modal -->
    <b-modal
      :active.sync="showModal"
      :width="800"
      scroll="keep"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <div class="modal-card" style="width: auto;">
        <header class="modal-card-head" style="background: white !important;">
          <p class="modal-card-title" style="color: #363636 !important;">
            Audit Log Details
          </p>
          <button
            type="button"
            class="delete"
            @click="showModal = false"
          ></button>
        </header>
        <section
          class="modal-card-body"
          style="background: white !important; color: #363636 !important;"
        >
          <div v-if="selectedLog" style="color: #363636 !important;">
            <table
              class="table is-fullwidth"
              style="background: white !important; color: #363636 !important;"
            >
              <tbody>
                <tr>
                  <th style="color: #363636 !important;">Timestamp</th>
                  <td style="color: #363636 !important;">
                    {{ formatDateTime(selectedLog.created_at) }}
                  </td>
                </tr>
                <tr>
                  <th style="color: #363636 !important;">User</th>
                  <td style="color: #363636 !important;">
                    <strong style="color: #363636 !important;">{{
                      selectedLog.user ? selectedLog.user.name : 'Unknown'
                    }}</strong
                    ><br />
                    {{ selectedLog.user ? selectedLog.user.email : '-' }}
                  </td>
                </tr>
                <tr>
                  <th style="color: #363636 !important;">Action</th>
                  <td style="color: #363636 !important;">
                    <b-tag :type="getActionColor(selectedLog.action)">{{
                      selectedLog.action
                    }}</b-tag>
                  </td>
                </tr>
                <tr>
                  <th style="color: #363636 !important;">Entity Type</th>
                  <td
                    style="text-transform: capitalize; color: #363636 !important;"
                  >
                    {{ selectedLog.table_name }}
                  </td>
                </tr>
                <tr>
                  <th style="color: #363636 !important;">Record ID</th>
                  <td style="color: #363636 !important;">
                    {{ selectedLog.record_id }}
                  </td>
                </tr>
                <tr v-if="selectedLog.media_info">
                  <th style="color: #363636 !important;">Media</th>
                  <td style="color: #363636 !important;">
                    <div style="font-weight: 600; color: #363636 !important;">
                      {{ selectedLog.media_info.title }}
                    </div>
                    <div
                      v-if="
                        selectedLog.media_info.type === 'series' &&
                          selectedLog.media_info.series_no
                      "
                      style="font-size: 12px; color: #7a7a7a !important; margin-top: 4px;"
                    >
                      Season {{ selectedLog.media_info.series_no }}, Episode
                      {{ selectedLog.media_info.episode_number }}
                      <span v-if="selectedLog.media_info.episode_title">
                        - {{ selectedLog.media_info.episode_title }}
                      </span>
                    </div>
                  </td>
                </tr>
                <tr v-if="selectedLog.enriched_info">
                  <th style="color: #363636 !important;">
                    {{
                      selectedLog.enriched_info.channel_name
                        ? 'Channel'
                        : 'Related User'
                    }}
                  </th>
                  <td style="color: #363636 !important;">
                    {{
                      selectedLog.enriched_info.channel_name ||
                        selectedLog.enriched_info.user_name
                    }}
                  </td>
                </tr>
                <tr v-if="selectedLog.old_values">
                  <th style="color: #363636 !important;">Old Values</th>
                  <td style="color: #363636 !important;">
                    <pre
                      style="background: #f5f5f5 !important; padding: 10px; border-radius: 4px; font-size: 12px; color: #363636 !important;"
                      >{{
                        JSON.stringify(
                          formatJSONWithTimezone(
                            selectedLog.old_values,
                            selectedLog.channel_timezone
                          ),
                          null,
                          2
                        )
                      }}</pre
                    >
                  </td>
                </tr>
                <tr v-if="selectedLog.new_values">
                  <th style="color: #363636 !important;">New Values</th>
                  <td style="color: #363636 !important;">
                    <pre
                      style="background: #f5f5f5 !important; padding: 10px; border-radius: 4px; font-size: 12px; color: #363636 !important;"
                      >{{
                        JSON.stringify(
                          formatJSONWithTimezone(
                            selectedLog.new_values,
                            selectedLog.channel_timezone
                          ),
                          null,
                          2
                        )
                      }}</pre
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import CardComponent from '@/components/CardComponent'
import axios from '@/axios'
import { formatInTimeZone } from 'date-fns-tz'

export default {
  name: 'AuditLogs',
  components: {
    CardComponent
  },
  data() {
    return {
      auditLogs: [],
      users: [],
      loading: false,
      search: '',
      filterUser: null,
      filterAction: null,
      filterEntity: null,
      showModal: false,
      selectedLog: null
    }
  },
  computed: {
    filteredLogs() {
      // Server-side filtering handles user/action/entity filters
      // Only apply client-side text search
      let logs = this.auditLogs

      if (this.search) {
        const searchLower = this.search.toLowerCase()
        logs = logs.filter(log => {
          const userName = log.user
            ? `${log.user.name} ${log.user.email}`.toLowerCase()
            : ''
          const action = log.action.toLowerCase()
          const entity = log.table_name.toLowerCase()
          const notes =
            log.new_values && log.new_values.notes
              ? log.new_values.notes.toLowerCase()
              : ''
          const mediaTitle = log.media_info
            ? log.media_info.title?.toLowerCase() || ''
            : ''

          return (
            userName.includes(searchLower) ||
            action.includes(searchLower) ||
            entity.includes(searchLower) ||
            notes.includes(searchLower) ||
            mediaTitle.includes(searchLower)
          )
        })
      }

      return logs
    }
  },
  created() {
    this.loadAuditLogs()
    this.loadUsers()
  },
  methods: {
    async loadAuditLogs() {
      this.loading = true
      try {
        // Build query params - filters applied on backend BEFORE limit
        // This ensures filtered results aren't cut off by the 500 entry limit
        const params = new URLSearchParams()
        params.append('limit', '500')

        if (this.filterUser) {
          params.append('user_id', this.filterUser)
        }
        if (this.filterAction) {
          params.append('action', this.filterAction)
        }
        if (this.filterEntity) {
          params.append('table_name', this.filterEntity)
        }

        const response = await axios.get(`/api/audit/recent?${params.toString()}`)
        this.auditLogs = response.data
        console.log('[AUDIT LOGS] Loaded audit logs:', response.data.length, 'entries')
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load audit logs',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },

    async loadUsers() {
      try {
        const response = await axios.get('/api/users')
        this.users = response.data
      } catch (error) {
        console.error('Failed to load users:', error)
      }
    },

    showDetails(log) {
      console.log('[AUDIT LOGS] Selected log:', log)
      console.log('[AUDIT LOGS] Media info:', log.media_info)
      console.log('[AUDIT LOGS] Channel timezone:', log.channel_timezone)
      this.selectedLog = log
      this.showModal = true
    },

    formatDate(timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatTime(timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    formatDateTime(timestamp) {
      return `${this.formatDate(timestamp)} ${this.formatTime(timestamp)}`
    },

    formatDuration(seconds) {
      if (!seconds) return '0:00'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${String(secs).padStart(2, '0')}`
    },

    getActionColor(action) {
      switch (action) {
        case 'create':
          return 'is-success'
        case 'update':
          return 'is-warning'
        case 'delete':
          return 'is-danger'
        default:
          return 'is-light'
      }
    },

    hasSegmentDurationChange(log) {
      return (
        log.table_name === 'segments' &&
        log.old_values &&
        log.new_values &&
        log.old_values.duration_seconds !== undefined &&
        log.new_values.duration_seconds !== undefined &&
        log.old_values.duration_seconds !== log.new_values.duration_seconds
      )
    },

    countChanges(log) {
      if (!log.old_values || !log.new_values) return 0

      let count = 0
      for (const key in log.new_values) {
        if (log.old_values[key] !== log.new_values[key]) {
          count++
        }
      }
      return count
    },

    truncate(text, length) {
      if (!text) return ''
      if (text.length <= length) return text
      return text.substring(0, length) + '...'
    },

    formatJSONWithTimezone(obj, timezone) {
      if (!obj || !timezone) return obj

      const formatted = {}
      for (const [key, value] of Object.entries(obj)) {
        // Check if this is a datetime string
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
          // Convert UTC to channel timezone
          formatted[key] = formatInTimeZone(
            new Date(value),
            timezone,
            'yyyy-MM-dd HH:mm:ss'
          )
        } else {
          formatted[key] = value
        }
      }
      return formatted
    }
  }
}
</script>

<style>
.card {
  box-shadow: none;
  color: #363636 !important;
}

.card .title {
  color: #363636 !important;
}

.card .table {
  color: #363636 !important;
}

.card .table th,
.card .table td {
  color: #363636 !important;
  width: auto;
  vertical-align: top;
  word-break: break-word;
}

.card .table th {
  width: 150px;
}

.card pre {
  max-height: 300px;
  overflow-y: auto;
  color: #363636 !important;
  background: #f5f5f5 !important;
}

.modal .card-content {
  color: #363636 !important;
}
</style>
